/**
 * Calculates OCR number for a given customer number.
 * 
 * OCR Structure: 999 + XXXXXXX + L + C
 *   - 999:      Fixed prefix
 *   - XXXXXXX:  Customer number (7 digits, zero-padded)
 *   - L:        Length digit = (currentLength + 2) % 10
 *   - C:        Control digit (Luhn algorithm)
 * 
 * Luhn Algorithm:
 *   1. Reverse the string
 *   2. Multiply digits alternately by 2 and 1 (starting with 2)
 *   3. If product > 9, subtract 9
 *   4. Sum all products
 *   5. Control = (10 - (sum % 10)) % 10
 * 
 * @param customerNumber - The customer number
 * @returns The calculated OCR number
 * 
 * @example
 * calculateOCR(226617) // Returns "999022661725"
 */
export const calculateOCR = (customerNumber: number | string): string => {
	// Build base string: "999" + customer number padded to 7 digits
	const baseString = "999" + String(customerNumber).padStart(7, "0");

	// Add length digit
	const lengthDigit = (baseString.length + 2) % 10;
	const withLengthDigit = baseString + String(lengthDigit);

	// Add control digit (Luhn)
	const controlDigit = calculateLuhnChecksum(withLengthDigit);

	return withLengthDigit + controlDigit;
};

/**
 * Calculates Luhn checksum (control digit)
 * @param value - The string to calculate checksum for
 * @returns The control digit (0-9)
 */
const calculateLuhnChecksum = (value: string): number => {
	const reversed = value.split("").reverse();
	let multiplier = 2;
	let sum = 0;

	for (const char of reversed) {
		let product = parseInt(char, 10) * multiplier;
		sum += product > 9 ? product - 9 : product;
		multiplier = multiplier === 2 ? 1 : 2;
	}

	const checksum = (10 - (sum % 10)) % 10;
	return checksum;
};